import React, { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  query,
  where,
  Timestamp,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebaseConfig.js";
import { useAuth } from "../context/AuthContext.js";
import axios from "axios";
import Papa from "papaparse";
import "./CRM.css";

const CRM = () => {
  const { currentUser } = useAuth();
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    status: "Prospecto",
    origin: "Recomendación",
    nextActivity: "",
    motivoNextActivity: "",
    proveniente: "",
    detalle: "",
    profesion: "",
    hijos: "",
    busca: "",
    ticket: "",
    cumple: "",
  });
  const [editMode, setEditMode] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [birthdayClients, setBirthdayClients] = useState([]);
  const [notification, setNotification] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [clientAnalysis, setClientAnalysis] = useState("");
  const [whatsappMessage, setWhatsappMessage] = useState("");
  const [prompt, setPrompt] = useState("");
  const [showPromptInput, setShowPromptInput] = useState(false);
  const [customPrompt, setCustomPrompt] = useState("");
  const [loadingAI, setLoadingAI] = useState(false);
  const clientsPerPage = 10;

  // Fetch clients
  const fetchClients = async () => {
    if (!currentUser) return;
  
    const q = query(
      collection(db, "clients"),
      where("createdBy", "==", currentUser.uid)
    );
  
    const querySnapshot = await getDocs(q);
  
    const clientList = querySnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        cumple: data.cumple && data.cumple.toDate ? data.cumple.toDate() : null,
        nextActivity: data.nextActivity && data.nextActivity.toDate ? data.nextActivity.toDate() : null,
      };
    });
  
    // Ordenar el array por nextActivity en orden ascendente
    clientList.sort((a, b) => {
      if (!a.nextActivity) return 1;
      if (!b.nextActivity) return -1;
      return a.nextActivity - b.nextActivity;
    });
  
    // Verificar cumpleaños
    const today = new Date();
    const todayFormatted = `${today.getMonth() + 1}-${today.getDate()}`;
  
    const birthdayList = clientList.filter((client) => {
      if (client.cumple) {
        const birthDate = new Date(client.cumple);
        const birthFormatted = `${birthDate.getMonth() + 1}-${birthDate.getDate()}`;
        return birthFormatted === todayFormatted;
      }
      return false;
    });
  
    setBirthdayClients(birthdayList);
  
    let notificationMessage = "";
  
    if (birthdayList.length > 0) {
      notificationMessage += `🎉 Hoy es el cumpleaños de ${birthdayList.map((c) => c.name).join(", ")}! `;
    }
  
    const now = new Date().toISOString().slice(0, 10);
    const clientsWithTodayActivity = clientList.filter(
      (client) =>
        client.nextActivity &&
        new Date(client.nextActivity).toISOString().slice(0, 10) === now
    );
  
    if (clientsWithTodayActivity.length > 0) {
      notificationMessage += `Tienes ${clientsWithTodayActivity.length} actividad(es) programada(s) para hoy.`;
    }
  
    setNotification(notificationMessage);
    setClients(clientList);
    setFilteredClients(clientList);
  };
  
   
  useEffect(() => {
    fetchClients();
  }, [currentUser]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    const filtered = clients.filter((client) =>
      client.name.toLowerCase().includes(e.target.value.toLowerCase())
    );
    setFilteredClients(filtered);
  };

  const handlePageChange = (direction) => {
    setCurrentPage((prevPage) => {
      const totalPages = Math.ceil(filteredClients.length / clientsPerPage);
      if (direction === "next" && prevPage < totalPages) return prevPage + 1;
      if (direction === "prev" && prevPage > 1) return prevPage - 1;
      return prevPage;
    });
  };

  
  const handleSaveClient = async () => {
    if (!currentUser) return;
    try {
      const dataToSave = {
        ...formData,
        createdBy: currentUser.uid,
        createdAt: Timestamp.now(),
        nextActivity: formData.nextActivity
          ? Timestamp.fromDate(new Date(formData.nextActivity))
          : null,
        cumple: formData.cumple
          ? Timestamp.fromDate(new Date(formData.cumple))
          : null,
      };
  
      // Validación de las fechas para evitar valores nulos o inválidos
      if (dataToSave.nextActivity && isNaN(dataToSave.nextActivity.seconds)) {
        throw new Error("Fecha de próxima actividad no válida.");
      }
      if (dataToSave.cumple && isNaN(dataToSave.cumple.seconds)) {
        throw new Error("Fecha de cumpleaños no válida.");
      }
  
      if (editMode) {
        await updateDoc(doc(db, "clients", selectedClient.id), dataToSave);
        setNotification("Cliente actualizado con éxito.");
      } else {
        await addDoc(collection(db, "clients"), dataToSave);
        setNotification("Cliente agregado con éxito.");
      }
  
      fetchClients();
      resetForm();
    } catch (error) {
      console.error("Error al guardar el cliente:", error);
      setNotification("Error al guardar el cliente.");
    }
  };
  
  const handleEditClient = (client) => {
    setEditMode(true);
    setSelectedClient(client);
    setFormData({
      name: client.name,
      email: client.email,
      phone: client.phone,
      status: client.status,
      origin: client.origin,
      nextActivity: client.nextActivity,
      motivoNextActivity: client.motivoNextActivity || "",
      proveniente: client.proveniente || "",
      detalle: client.detalle || "",
      profesion: client.profesion || "",
      hijos: client.hijos || "",
      busca: client.busca || "",
      ticket: client.ticket || "",
      cumple: client.cumple,
    });
    setShowForm(true);
  };

  const handleDeleteClient = async (clientId) => {
    try {
      await deleteDoc(doc(db, "clients", clientId));
      setNotification("Cliente eliminado con éxito.");
      fetchClients();
    } catch (error) {
      console.error("Error al eliminar el cliente:", error);
      setNotification("Error al eliminar el cliente.");
    }
  };

  const resetForm = () => {
    setEditMode(false);
    setFormData({
      name: "",
      email: "",
      phone: "",
      status: "Prospecto",
      origin: "Recomendación",
      nextActivity: "",
      motivoNextActivity: "",
      proveniente: "",
      detalle: "",
      profesion: "",
      hijos: "",
      busca: "",
      ticket: "",
      cumple: "",
    });
    setShowForm(false);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
  
    // Validar que el archivo tenga la extensión .csv
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (fileExtension !== "csv") {
      setNotification("Solo se permiten archivos .csv. Por favor, guarda tu hoja de cálculo en este formato antes de subirla.");
      return;
    }
  
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const rows = results.data;
          for (const row of rows) {
            const newClient = {
              name: row["Nombre"] || "",
              email: row["Email"] || "",
              phone: row["Teléfono"] || "",
              status: row["Estado"] || "Prospecto",
              profesion: row["Profesión"] || "",
              hijos: row["Hijos"] || "",
              busca: row["¿Qué busca?"] || "",
              detalle: row["Detalle"] || "",
              ticket: row["Ticket"] || "",
              cumple: row["Cumpleaños"]
                ? Timestamp.fromDate(new Date(row["Cumpleaños"] + "T00:00:00Z"))
                : null,
              origin: row["Origen"] || "",
              nextActivity: row["Próxima Actividad"]
                ? Timestamp.fromDate(new Date(row["Próxima Actividad"] + "T00:00:00Z"))
                : null,
              createdBy: currentUser.uid,
              createdAt: Timestamp.now(),
            };
  
            // Validación de las fechas para evitar valores nulos o inválidos
            if (newClient.nextActivity && isNaN(newClient.nextActivity.seconds)) {
              throw new Error("Fecha de próxima actividad no válida.");
            }
            if (newClient.cumple && isNaN(newClient.cumple.seconds)) {
              throw new Error("Fecha de cumpleaños no válida.");
            }
  
            await addDoc(collection(db, "clients"), newClient);
          }
  
          setNotification("Archivo cargado y clientes agregados con éxito.");
          fetchClients();
        } catch (error) {
          console.error("Error al procesar el archivo:", error);
          setNotification("Error al cargar el archivo.");
        }
      },
      error: (error) => {
        console.error("Error al leer el archivo:", error);
        setNotification("Error al leer el archivo.");
      },
    });
  };

  useEffect(() => {
    const checkNextActivities = () => {
      const now = new Date().toISOString().slice(0, 10); // Solo la fecha (YYYY-MM-DD)
      const clientsWithTodayActivity = clients.filter(
        (client) =>
          client.nextActivity &&
          new Date(client.nextActivity).toISOString().slice(0, 10) === now
      );
  
      if (clientsWithTodayActivity.length > 0) {
        setNotification(
          `Tienes ${clientsWithTodayActivity.length} actividad(es) programada(s) para hoy.`
        );
      }
    };
  
    const interval = setInterval(checkNextActivities, 60000); // Verifica cada minuto
    checkNextActivities(); // Llama inmediatamente al cargar el componente
  
    return () => clearInterval(interval); // Limpia el intervalo al desmontar
  }, [clients]);

  const handleUpdateNextActivity = async (clientId, newDate) => {
    try {
      await updateDoc(doc(db, "clients", clientId), {
        nextActivity: newDate ? Timestamp.fromDate(new Date(newDate + "Z")) : null,
      });
      setNotification("Próxima actividad actualizada.");
      fetchClients();
    } catch (error) {
      console.error("Error al actualizar la fecha:", error);
      setNotification("Error al actualizar la fecha.");
    }
  };
  
  const normalizeNextActivity = (nextActivity) => {
    if (!nextActivity) return null; // Si es null o undefined
    if (nextActivity.toDate) return nextActivity.toDate(); // Si es un Timestamp
    if (typeof nextActivity === "string") return new Date(nextActivity); // Si es una cadena
    if (nextActivity instanceof Date) return nextActivity; // Si ya es un Date
    return null; // Si no se reconoce el formato
  };
  
  function processStrongTags(text) {
    // Usamos una expresión regular para encontrar las palabras o frases entre **
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  }
  
  const handleAnalyzeClient = async (client) => {
    setSelectedClient(client);
    setLoadingAI(true);
    setClientAnalysis("");
    setWhatsappMessage("");
  
    if (!client) {
      console.error("Cliente no disponible para análisis.");
      return;
    }
  
    // Obtener el usuario autenticado
    const user = currentUser.uid;
    if (!user) {
      console.error("Usuario no autenticado.");
      setClientAnalysis("Debe iniciar sesión para generar un análisis.");
      setLoadingAI(false);
      return;
    }
  
    // Valores predeterminados si faltan datos del cliente
    const clientName = client.name || "Nombre no disponible";
    const clientEmail = client.email || "Correo no disponible";
    const clientPhone = client.phone || "Teléfono no disponible";
    const clientCumple = client.cumple || "No disponible";
    const clientHijos = client.hijos || "No especificado";
    const clientProfesion = client.profesion || "No especificado";
    const clientTicket = client.ticket || "No especificado";
    const clientStatus = client.status || "No especificado";
    const clientOrigin = client.origin || "No especificado";
    const clientNextActivity = client.nextActivity ? client.nextActivity.toString() : "No definida";
    const clientDetails = client.detalle || "No detalles disponibles";
    const clientBusca = client.busca || "No especificado";
  
    try {
      // Obtener datos de la empresa asociada al usuario
      const empresaRef = collection(db, "empresas");
      const q = query(empresaRef, where("userId", "==", currentUser.uid));
      const querySnapshot = await getDocs(q);
      
      let empresaData = {};
      if (!querySnapshot.empty) {
        empresaData = querySnapshot.docs[0].data();
      }
  
      // Extraer la información de la empresa
      const empresaNombre = empresaData.companyName || "Empresa no registrada";
      const empresaIndustry = empresaData.industry || "Sector no especificado";
      const empresaSector = empresaData.industryDetail || "Detalle no especificado";
  
      // Construcción del prompt con información del cliente y la empresa
      const prompt = `
      Solicito un análisis detallado basado en la información del cliente y la empresa.
  
      **Datos del Cliente**:
      - Nombre: ${clientName}
      - Correo: ${clientEmail}
      - Teléfono: ${clientPhone}
      - Cumpleaños: ${clientCumple}
      - Hijos: ${clientHijos}
      - Profesión: ${clientProfesion}
      - Ticket: ${clientTicket}
      - Estado: ${clientStatus}
      - Origen: ${clientOrigin}
      - Actividad próxima: ${clientNextActivity}
      - Detalles adicionales: ${clientDetails}
      - Necesidad de compra: ${clientBusca}
  
      **Datos de la Empresa**:
      - Nombre: ${empresaNombre}
      - Sector: ${empresaIndustry}
      - Se dedica: ${empresaSector}
  
      **Tareas de Análisis**:
      1. **Evaluación de la necesidad de compra**: Analiza la información del campo "busca" para entender qué necesita el cliente. Considera que la empresa es del sector "${empresaIndustry}" y se especializa en "${empresaSector}" para hacer recomendaciones precisas.
      2. **Análisis de comunicación**: Basado en el sector de la empresa, cómo mejorar la relación con el cliente.
      3. **Optimización de conversión**: Estrategias según el tipo de negocio y estado del cliente.
      4. **Estrategias de ventas y marketing personalizadas**: Enfoque en cómo adaptar la estrategia comercial.
      5. **Organización de actividades**: Cómo organizar la próxima actividad con el cliente.
  
      Responde en un tono persuasivo y profesional, orientado a la conversión del cliente. También responde como un experto en ventas, marketing y psicologia humana para entender los datos y dar la mejor respuesta posible. Máximo 210 palabras.
      `;
  
      // Llamado a la API de OpenAI
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      
      const response = await axios.post(
        "https://api.openai.com/v1/chat/completions",
        {
          model: "gpt-3.5-turbo",
          messages: [{ role: "user", content: prompt }],
          max_tokens: 500,
          temperature: 0.7,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
        }
      );
  
      // Obtener la respuesta de la IA
      const analysis = response.data.choices[0].message.content;
  
      // Procesamos la respuesta con la función processStrongTags
      const clientAnalysisProcessed = processStrongTags(analysis);
  
      // Actualizamos el estado con la respuesta procesada
      setClientAnalysis(clientAnalysisProcessed);
    } catch (error) {
      console.error("Error en el análisis del cliente:", error);
      setClientAnalysis("Ocurrió un error al generar el análisis.");
    } finally {
      setLoadingAI(false);
    }
  };
  
  

  const paginatedClients = filteredClients.slice(
    (currentPage - 1) * clientsPerPage,
    currentPage * clientsPerPage
  );

  return (
    <div className="crm-container">
      <h1 className="crm-title">CRM de Clientes</h1>

      <div className="crm-toolbar">
        <input
          type="text"
          placeholder="Buscar Cliente"
          value={search}
          onChange={handleSearch}
          className="form-control"
        />
        <div className="file-upload-container">
                <label htmlFor="file-upload" className="custom-file-label">
                <i className="fa fa-file-upload"></i>
                <span>Subir archivo</span>
            </label>
            <small>Carga tu Excel con tu lista de clientes</small>
            <input
                id="file-upload"
                type="file"
                accept=".csv, .xlsx"
                onChange={handleFileUpload}
                className="hidden-input"
            /> 
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {editMode ? "Editar Cliente" : "Agregar Cliente"}
        </button>
        
      </div>

      {showForm && (
        <div className="crm-form">
          <div className="crm-input">
          <label htmlFor="nombre">Nombre</label>
          <input
            type="text" className="form-control"
            placeholder="Nombre"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          </div>
          <div className="crm-input">
          <label htmlFor="email">Email</label>
          <input
            type="email" className="form-control"
            placeholder="Email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          </div>
          <div className="crm-input">
          <label htmlFor="telefono">Teléfono</label>
          <input
            type="text" className="form-control"
            placeholder="Teléfono"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
          </div>
          <div className="crm-input">
          <label htmlFor="estado">Estado</label>
          <select className="form-control"
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
          >
            <option value="Prospecto">Prospecto</option>
            <option value="Cliente">Cliente</option>
            <option value="Inactivo">Inactivo</option>
          </select>
          </div>
          
            <div className="crm-input">
            <label htmlFor="proviene">Proveniente</label>
            <input
            type="text" className="form-control"
            name="proveniente"
            value={formData.proveniente}
            onChange={(e) => setFormData({ ...formData, proveniente: e.target.value })}

            placeholder="¿De dónde proviene?"
            />
            </div>
            <div className="crm-input">
            <label htmlFor="profesion">Profesión</label>
            <input type="text" className="form-control" name="profesion" placeholder="Profesión" value={formData.profesion} onChange={(e) => setFormData({ ...formData, profesion: e.target.value })}/>
            </div>
            <div className="crm-input">
            <label htmlFor="hijos">Hijos</label>
            <input type="text" className="form-control" name="hijos" placeholder="Hijos" value={formData.hijos} onChange={(e) => setFormData({ ...formData, hijos: e.target.value })}/>
            </div>
            <div className="crm-input">
            <label htmlFor="busca">Busca:</label>
            <input type="text" className="form-control" name="busca" placeholder="¿Qué busca?" value={formData.busca} onChange={(e) => setFormData({ ...formData, busca: e.target.value })} />
            </div>
            <div className="crm-input">
            <label htmlFor="ticket">Ticket</label>
            <input type="text" className="form-control" name="ticket" placeholder="Ticket" value={formData.ticket} onChange={(e) => setFormData({ ...formData, ticket: e.target.value })}  />
            </div>
            <div className="crm-input">
            <label htmlFor="cumple">Cumpleaños</label>
            <input type="datetime-local" className="form-control" name="cumple" placeholder="Cumpleaños" 
            value={
              formData.cumple && formData.cumple.seconds
                ? new Date(formData.cumple.seconds * 1000).toISOString().slice(0, 16)
                : ''
            }
             onChange={(e) => setFormData({ ...formData, cumple: e.target.value })} />
            </div>
            
          <label htmlFor="nextActivity">Próxima Actividad</label>
          <input
            type="datetime-local" className="form-control"
            value={formData.nextActivity}
            onChange={(e) =>
              setFormData({ ...formData, nextActivity: e.target.value })
            }
          />
          
          <label htmlFor="motivo">Motivo de la próxima actividad</label>
          <input
          type="text" className="form-control"
          name="motivoNextActivity"
          value={formData.motivoNextActivity}
          onChange={(e) => setFormData({ ...formData, motivoNextActivity: e.target.value })}
          placeholder="Motivo del Siguiente Contacto"
          />
          
          <label htmlFor="detalle">Detalles</label>
            <input
            type="text" className="form-control"
            name="detalle"
            value={formData.detalle}
            onChange={(e) => setFormData({ ...formData, detalle: e.target.value })}

            placeholder="Detalles Adicionales"
            />
          <div className="botones-crm">
          <button onClick={handleSaveClient} className="btn btn-success">
            Guardar
          </button>
          <button className="btn btn-secondary" onClick={resetForm}>
            Cancelar
          </button>
          </div>
        </div>
      )}

        {notification && (
        <div className="alert alert-info-act">
            {notification}
            <button
            onClick={() => setNotification("")}
            className="close-btn"
            aria-label="Cerrar"
            >
            &times;
            </button>
        </div>
        )}

      <div className="contCumple">
        <h2>Clientes Cumpliendo Hoy</h2>
        {birthdayClients.length > 0 ? (
          <ul>
            {birthdayClients.map((client) => (
              <li className="notification-banner" key={client.id}>{client.name}</li>
            ))}
          </ul>
        ) : (
          <p>No hay cumpleaños hoy.</p>
        )}
      </div>

      <table className="table table-striped">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Estado</th>
            <th>Próxima Actividad</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
        {paginatedClients.map((client) => {
            // Normalizamos la fecha de nextActivity para cada cliente
            const activityDate = normalizeNextActivity(client.nextActivity); 
            const isToday =
                activityDate &&
                activityDate.toDateString() === new Date().toDateString(); // Verificamos si es hoy

            return (
                <tr
                key={client.id}
                style={{
                    backgroundColor: isToday ? "#007aff" : "transparent",
                }}
                >
                <td style={{
                    color: isToday ? "#ffffff" : "inherit",
                }}>{client.name}</td>
                <td style={{
                    color: isToday ? "#ffffff" : "inherit",
                }}>{client.status}</td>
                <td style={{
                    color: isToday ? "#ffffff" : "inherit",
                }}>{activityDate ? activityDate.toLocaleDateString() : "No programada"}</td>
                <td className="btns-actions">
                    <a
                      href={`https://wa.me/${client.phone}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-success btn-sm"
                    >
                      <i className="fa-brands fa-whatsapp"></i>
                    </a>
                    <button
                      className="btn btn-info btn-sm"
                      onClick={() => handleAnalyzeClient(client)}  
                    >
                      IA
                    </button>
                    <button
                    className="btn btn-warning btn-sm"
                    onClick={() => handleEditClient(client)}
                    >
                    <i className="fa fa-edit"></i>
                    </button>
                    <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleDeleteClient(client.id)}
                    >
                    <i className="fa fa-trash"></i>
                    </button>
                </td>
                </tr>
            );
            })}
        </tbody>
      </table>


       {/* Mostrar el análisis y el mensaje de WhatsApp en contenedores separados */}
       {loadingAI ? (
        <p>Generando análisis...</p>
      ) : (
        <>
          <div className="containerAnalisisIa">
            <h3>Análisis del Cliente</h3>
            <p
            dangerouslySetInnerHTML={{ __html: clientAnalysis }}
          />
          </div>
        </>
      )}

      <div className="crm-pagination">
        <button
          className="btn btn-outline-secondary"
          onClick={() => handlePageChange("prev")}
          disabled={currentPage === 1}
        >
          Anterior
        </button>
        <button
          className="btn btn-outline-secondary"
          onClick={() => handlePageChange("next")}
          disabled={
            currentPage >= Math.ceil(filteredClients.length / clientsPerPage)
          }
        >
          Siguiente
        </button>
      </div>
    </div>
  );
};

export default CRM;
